import { aiStrategyTradesListenRequest, aiStrategyTradesStopRequest } from "./api-request-builders/ai-strategy-trades-request.builder";
import { CancelToken } from "./connection.client";
import { AiStrategyTradesJSONToResponseModelMapper } from "./mappers/ai-strategy-trades-json-to-response-model.mapper";
import { AiStrategyTrade } from "./models/ai/ai-strategy-trade";
import { AiSegment } from "./models/ai/ai-segment";
import { AiStrategy } from "./models/ai/ai-strategy";

function pascalToCamelCaseKeys(obj: any): any {
    return Object.fromEntries(
        Object.entries(obj).map(([k, v]) => [k.charAt(0).toLowerCase() + k.slice(1), v])
    );
}

export class AiStrategyTradesClient {
    private cancelToken: CancelToken | null = null;
    private subscriptionId: string | null = null;

    public subscribe(
        onData: (trades: AiStrategyTrade[], segment: string) => void,
        onSubscribed?: (subscriptionId: string, segments: AiSegment[], strategies: AiStrategy[]) => void
    ): CancelToken {
        if (this.cancelToken) {
            this.cancelToken();
            this.cancelToken = null;
        }

        this.cancelToken = aiStrategyTradesListenRequest((response: string) => {
            let parsed: any;
            try {
                parsed = JSON.parse(response);
            } catch {
                return;
            }

            const subCommand: string = parsed.SubCommand ?? "";

            if (subCommand === "ai_strategy_trades_subscribe_success") {
                this.subscriptionId = parsed.SubscriptionId ?? null;

                if (onSubscribed) {
                    const segments: AiSegment[] = (parsed.Segments ?? []).map((s: any) =>
                        new AiSegment(s.Name ?? "", s.Code ?? "")
                    );

                    const strategies: AiStrategy[] = (parsed.Strategies ?? []).map((s: any) => {
                        const strategy = new AiStrategy();
                        strategy.name = s.Name;
                        strategy.winRate = s.WinRate;
                        strategy.profitFactor = s.ProfitFactor;
                        strategy.config = s.Config;
                        strategy.isLong = s.IsLong ?? false;
                        strategy.timeFrame = s.TimeFrame;
                        strategy.totalTrades = s.TotalTrades ?? 0;
                        strategy.maxTradesPerDay = s.MaxTradesPerDay ?? 0;
                        strategy.cloudLink = s.CloudLink;
                        strategy.profitPerDay = s.ProfitPerDay;
                        strategy.optimizedDate = s.OptimizedDate ? new Date(s.OptimizedDate) : undefined;
                        strategy.creationDate = s.CreationDate ? new Date(s.CreationDate) : undefined;
                        strategy.segmentCode = s.SegmentCode;
                        strategy.segmentName = s.SegmentName;
                        return strategy;
                    });

                    onSubscribed(this.subscriptionId!, segments, strategies);
                }
            } else if (subCommand === "ai_strategy_trades_data") {
                const trades = AiStrategyTradesJSONToResponseModelMapper.map(
                    (parsed.Trades ?? []).map(pascalToCamelCaseKeys)
                );
                onData(trades, parsed.Segment ?? "");
            }
        });

        return this.cancelToken;
    }

    public unsubscribe(): void {
        if (this.cancelToken) {
            this.cancelToken();
            this.cancelToken = null;
        }

        if (this.subscriptionId) {
            aiStrategyTradesStopRequest(this.subscriptionId);
            this.subscriptionId = null;
        }
    }
}
